import { useRef, useState } from 'react';
import { Film, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const CHUNK_SIZE = 4 * 1024 * 1024;
const MAX_VIDEO_MB = 300;
const ACCEPTED = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v'];

async function uploadVideoChunked(file, onProgress) {
  const uploadId = crypto.randomUUID();
  const total = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < total; i++) {
    const form = new FormData();
    form.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
    await api.post(`/admin/upload/video/chunk?upload_id=${uploadId}&index=${i}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0,
    });
    onProgress(Math.round(((i + 1) / total) * 90));
  }
  const res = await api.post('/admin/upload/video/complete', {
    upload_id: uploadId, total_chunks: total, filename: file.name, content_type: file.type,
  }, { timeout: 0 });
  return res.data;
}

function capturePoster(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = URL.createObjectURL(file);
    const done = (blob) => { URL.revokeObjectURL(video.src); resolve(blob); };
    video.onerror = () => done(null);
    video.onloadedmetadata = () => { video.currentTime = Math.min(1, (video.duration || 1) / 2); };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        canvas.toBlob((blob) => done(blob), 'image/jpeg', 0.85);
      } catch (e) { done(null); }
    };
    setTimeout(() => done(null), 8000);
  });
}

async function uploadPoster(blob, name) {
  const form = new FormData();
  form.append('file', new File([blob], `${name}-poster.jpg`, { type: 'image/jpeg' }));
  const res = await api.post('/admin/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  return res.data;
}

export default function VideoUploader({ onUploaded, label = 'Carica Video', size = 'sm', variant = 'outline', testId = 'video-upload-btn' }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ACCEPTED.includes(file.type) && !['mp4', 'webm', 'mov', 'm4v'].includes(ext)) {
      toast.error('Formato non supportato. Usa MP4, WebM o MOV');
      return;
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Video troppo grande (${(file.size / 1024 / 1024).toFixed(0)} MB). Massimo ${MAX_VIDEO_MB} MB`);
      return;
    }
    setProgress(0);
    try {
      const posterPromise = capturePoster(file);
      const video = await uploadVideoChunked(file, setProgress);
      setProgress(95);
      let poster = null;
      const blob = await posterPromise;
      if (blob) {
        try { poster = await uploadPoster(blob, video.id); } catch (err) { poster = null; }
      }
      setProgress(100);
      onUploaded({
        ...video,
        thumbnail_url: poster?.thumbnail_url || '',
        poster_url: poster?.url || '',
        alt_text: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '),
      });
      toast.success('Video caricato. Ottimizzazione per mobile (720p) in corso in background');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Caricamento video fallito');
    } finally {
      setTimeout(() => setProgress(null), 600);
    }
  };

  const busy = progress !== null;

  return (
    <div className="inline-flex flex-col gap-2">
      <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
      <Button type="button" variant={variant} size={size} disabled={busy} onClick={() => inputRef.current?.click()} data-testid={testId}>
        {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Film size={14} className="mr-1" />}
        {busy ? `Caricamento ${progress}%` : label}
      </Button>
      {busy && <Progress value={progress} className="h-1.5 w-full" data-testid={`${testId}-progress`} />}
    </div>
  );
}
